import { Plus } from "lucide-react";
import React, { useContext, useEffect } from "react";
import { useCookies } from "react-cookie";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import tips from "../../../../assets/copy/tips";
import Loader from "../../../../components/Loader";
import EducationSummaryBox from "./../../../../components/ResumeBuilder/EducationSummaryBox/index";
import Tips from "../../../../components/Tips";
import { Button, buttonVariants } from "../../../../components/ui/button";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import { resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import { nextAfterEducation, skipFromEducationSummary, useEducationFirst } from "../../../../router/sectionOrder";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_ACTIONS, RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import type { ResumeContextValue } from "../../../../types/resume";

const ResumeEducationSummary = () => {
	const navigate = useNavigate();

	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	let { education, workHistory, journal } = resumeData;

	let resumeCurrentStatus = journal ? journal.currentStatus : false;
	let isComplete = resumeCurrentStatus == "COMPLETED";

	const educationFirst = useEducationFirst();

	// Work History for a reader with no experience, Skills for everyone else — they
	// were asked for work history before this screen.
	const nextSection = nextAfterEducation(educationFirst, {
		educationCount: education.length,
		workHistoryCount: workHistory.length,
	});

	const [loading, setLoading] = React.useState(true);
	useEffect(() => {
		if (resumeData.contactInfo.firstName === "") {
			return setLoading(true);
		}
		return setLoading(false);
	}, [resumeData]);

	const skipOne = skipFromEducationSummary(educationFirst, workHistory.length);

	// Label from the same `educationFirst` flag that picks `nextSection`, so the
	// button can never advertise a different screen than the one it opens. The
	// legacy NextButton read a stale flow pointer and printed the step the reader
	// had just finished.
	const nextTxt = isComplete ? "Save & Close" : `Next : ${educationFirst ? "Work History" : "Skills"}`;

	function next(e) {
		e.preventDefault();

		let nextLink = navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete, skipOne).value
			.url;

		if (nextLink === "review") return navigate("/my-resumes");
		return navigate(`/resume/${nextLink}`);
	}

	useEffect(() => {}, [resumeData]);

	const addAnotherEduHandler = () => {
		navigationHandler.getPrevious(navigationHandler.template.navigationFlow, link, isComplete, 0);

		return navigate("/resume/education-information");
	};

	function onSuccess(response) {
		resumeObjectToContextFormat(response, setResumeData);
	}

	function onError() {
		toast.error("Something went wrong");
	}
	const [cookies] = useCookies(["resume"]);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const resumeId = cookies[staticValues.localStorageResumeId];

	const patchSection = usePatchResumeSection();

	const deleteHandler = (eduId) => {
		patchSection.mutate(
			{
				resumeId,
				section: RESUME_SECTIONS.EDUCATION,
				data: { referenceId: eduId },
				action: RESUME_ACTIONS.DELETE,
			} as never,
			{ onSuccess, onError },
		);
	};

	useEffect(() => {
		if (!loading && education.length === 0) {
			navigate("/resume/education-information");
		}
	}, [education]);

	const summaryBox = education.map((summaryItem, index) => (
		<EducationSummaryBox
			item={summaryItem}
			deleteEduSummary={deleteHandler}
			key={index}
			index={summaryItem.referenceId}
		/>
	));

	useEffect(() => {}, [resumeData]);

	return (
		<div className="min-h-screen">
			<div className="md:w-2/3">
				<div className="mb-2 flex justify-end">
					<Tips content={tips.educationHistory} />
				</div>
			</div>

			{loading ? (
				<Loader />
			) : (
				<div className="md:w-2/3">
					{summaryBox}
					<Button type="button" variant="link" className="font-semibold cursor-pointer" onClick={addAnotherEduHandler}>
						<Plus className="mr-3 size-4" />
						Add another education
					</Button>
					<div className="mt-4 flex items-center justify-end">
						{/* Same target `next` resolves to via `skipOne` — see WorkHistorySummary
						    for why the dead `to` still has to be right. */}
						<Link to={`/resume/${nextSection}`} className={buttonVariants()} onClick={next}>
							{nextTxt}
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};

export default ResumeEducationSummary;
